<template>
  <div class="dw-grid-wrap">
    <grid-layout
      :layout.sync="layout"
      :col-num="4"
      :row-height="60"
      :margin="[16, 16]"
      :is-draggable="editMode"
      :is-resizable="editMode"
      :vertical-compact="true"
      :use-css-transforms="true"
      :class="{ 'dw-editing': editMode }"
      @layout-updated="onLayoutUpdated"
    >
      <grid-item
        v-for="item in layout"
        :key="item.i"
        :i="item.i"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :min-w="item.minW"
        :min-h="item.minH"
        :max-h="item.maxH"
      >
        <button
          v-if="editMode"
          type="button"
          class="dw-remove"
          title="Remove widget"
          @mousedown.stop
          @click="removeWidget(item.i)"
        >
          <a-icon type="close" />
        </button>

        <PortfolioWidget
          v-if="item.i === 'portfolio'"
          :data="portfolio.data"
          :archived="archivedUrls.data"
          :loading="portfolio.loading"
          :error="portfolio.error"
        />
        <TodaysAlertsWidget
          v-else-if="item.i === 'today'"
          :data="today.data"
          :severity="severityToday.data"
          :severityError="severityToday.error"
          :aiTriage="aiTriage"
          :loading="today.loading"
          :error="today.error"
        />
        <BlackoutCoverageWidget
          v-else-if="item.i === 'blackout'"
          :data="blackout.data"
          :loading="blackout.loading"
          :error="blackout.error"
        />
        <AlertsTotalTrendWidget
          v-else-if="item.i === 'totalTrend'"
          :ref="'w_' + item.i"
          :tagIds="tagIds"
          :planTypes="planTypes"
          :reloadToken="reloadToken"
        />
        <TopNoisyUrlsWidget
          v-else-if="item.i === 'topNoisy'"
          :ref="'w_' + item.i"
          :tagIds="tagIds"
          :planTypes="planTypes"
          :reloadToken="reloadToken"
        />
        <AlertTrendWidget
          v-else-if="item.i === 'trend'"
          :ref="'w_' + item.i"
          :tagIds="tagIds"
          :planTypes="planTypes"
          :reloadToken="reloadToken"
        />
        <TopPagesWidget
          v-else-if="item.i === 'topPages'"
          :ref="'w_' + item.i"
          :tagIds="tagIds"
          :planTypes="planTypes"
          :reloadToken="reloadToken"
        />
        <PlanUtilisationWidget
          v-else-if="item.i === 'planUtil'"
          :ref="'w_' + item.i"
          :reloadToken="reloadToken"
        />
        <AlertsByFqdnTagWidget
          v-else-if="item.i === 'fqdnTag'"
          :ref="'w_' + item.i"
          :reloadToken="reloadToken"
        />
        <UpcomingCrawlsWidget
          v-else-if="item.i === 'upcomingCrawls'"
          :ref="'w_' + item.i"
          :reloadToken="reloadToken"
        />
        <UpcomingDomainCrawlsWidget
          v-else-if="item.i === 'upcomingDomainCrawls'"
          :ref="'w_' + item.i"
          :reloadToken="reloadToken"
        />
        <ChangeEventsSummaryWidget
          v-else-if="item.i === 'changeEventsSummary'"
          :ref="'w_' + item.i"
          :reloadToken="reloadToken"
        />
        <OpenSiteIncidentsWidget
          v-else-if="item.i === 'openSiteIncidents'"
          :ref="'w_' + item.i"
          :reloadToken="reloadToken"
        />
      </grid-item>
    </grid-layout>
  </div>
</template>

<script>
  import { GridLayout, GridItem } from 'vue-grid-layout';
  import DashboardService from '../../services/dashboard.service';
  import {
    WIDGETS,
    DASHBOARD_VERSION,
    buildDefaultLayout,
    hydrateSavedLayout,
    appendNewDefaults,
    isEntitled,
    widgetLayoutItem
  } from './widgetRegistry';
  import { layoutStore } from './layoutStore';
  import { clearMetricsCache, setCacheScope, primeCache } from '../../services/metricsCache';
  import PortfolioWidget from './PortfolioWidget.vue';
  import TodaysAlertsWidget from './TodaysAlertsWidget.vue';
  import BlackoutCoverageWidget from './BlackoutCoverageWidget.vue';
  import AlertsTotalTrendWidget from './AlertsTotalTrendWidget.vue';
  import AlertTrendWidget from './AlertTrendWidget.vue';
  import TopNoisyUrlsWidget from './TopNoisyUrlsWidget.vue';
  import TopPagesWidget from './TopPagesWidget.vue';
  import PlanUtilisationWidget from './PlanUtilisationWidget.vue';
  import AlertsByFqdnTagWidget from './AlertsByFqdnTagWidget.vue';
  import UpcomingCrawlsWidget from './UpcomingCrawlsWidget.vue';
  import UpcomingDomainCrawlsWidget from './UpcomingDomainCrawlsWidget.vue';
  import ChangeEventsSummaryWidget from './ChangeEventsSummaryWidget.vue';
  import OpenSiteIncidentsWidget from './OpenSiteIncidentsWidget.vue';

  const initialState = () => ({ data: null, loading: true, error: false });

  // Widgets whose data (and cache key) ignores the dashboard filters.
  const ACCOUNT_WIDE = new Set(['planUtil', 'fqdnTag']);

  // What the add-widget modal needs to render and group a widget.
  const widgetSummary = (i) => ({
    i,
    group: WIDGETS[i].group,
    icon: WIDGETS[i].icon,
    title: WIDGETS[i].title,
    description: WIDGETS[i].description,
    requiresFeature: WIDGETS[i].requiresFeature || null
  });

  export default {
    name: 'DashboardGrid',
    components: {
      GridLayout,
      GridItem,
      PortfolioWidget,
      TodaysAlertsWidget,
      BlackoutCoverageWidget,
      AlertsTotalTrendWidget,
      AlertTrendWidget,
      TopNoisyUrlsWidget,
      TopPagesWidget,
      PlanUtilisationWidget,
      AlertsByFqdnTagWidget,
      UpcomingCrawlsWidget,
      UpcomingDomainCrawlsWidget,
      ChangeEventsSummaryWidget,
      OpenSiteIncidentsWidget
    },
    props: {
      tagIds: { type: Array, default: () => [] },
      planTypes: { type: Array, default: () => [] },
      // Owned by Home so the edit controls can live in the filter row.
      editMode: { type: Boolean, default: false }
    },
    data() {
      return {
        layout: [],
        // Bumped to reload self-fetching widgets.
        reloadToken: 0,
        // Staleness guard: each load context bumps it; superseded async results
        // are discarded so stale data never lands on the current layout.
        loadToken: 0,
        portfolio: initialState(),
        archivedUrls: initialState(),
        today: initialState(),
        severityToday: initialState(),
        blackout: initialState()
      };
    },
    computed: {
      userKey() {
        const u = this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.data;
        return u && u.id != null ? String(u.id) : 'anonymous';
      },
      storageKey() {
        return `${this.userKey}:v${DASHBOARD_VERSION}`;
      },
      // Which default widgets this account has already been offered, so a new
      // default is appended once and a removed one stays removed.
      offeredKey() {
        return `${this.storageKey}:offered`;
      },
      featureAccess() {
        return this.$store.getters.featureAccess;
      },
      // Passed to TodaysAlertsWidget, which changes layout rather than gating.
      aiTriage() {
        return Boolean(this.featureAccess['alert-triage']);
      },
      presentIds() {
        return this.layout.reduce((set, w) => set.add(w.i), new Set());
      },
      addableWidgets() {
        return Object.keys(WIDGETS)
          .filter((i) => !this.presentIds.has(i) && isEntitled(i, this.featureAccess))
          .map(widgetSummary);
      },
      // Missing the required feature; shown disabled in the add modal.
      lockedWidgets() {
        return Object.keys(WIDGETS)
          .filter((i) => !this.presentIds.has(i) && !isEntitled(i, this.featureAccess))
          .map(widgetSummary);
      },
      // One key for both filters, so a commit reloads once rather than twice.
      filterKey() {
        return JSON.stringify([this.tagIds, this.planTypes]);
      }
    },
    watch: {
      filterKey() {
        this.reloadBatched();
      },
      // Metric URLs are shared across users, so drop the cache on user change.
      userKey(id) {
        setCacheScope(id);
      }
    },
    mounted() {
      setCacheScope(this.userKey);
      this.initLayout();
      this.emitState();
      this.loadAll();
    },
    methods: {
      // Surface add/locked lists to Home's add modal; re-emit on layout change.
      emitState() {
        this.$emit('state', { addable: this.addableWidgets, locked: this.lockedWidgets });
      },
      initLayout() {
        const saved = layoutStore.load(this.storageKey);
        const hydrated = hydrateSavedLayout(saved, this.featureAccess);
        if (!hydrated.length) {
          this.layout = buildDefaultLayout(this.featureAccess);
          layoutStore.save(this.offeredKey, this.layout.map((w) => w.i));
          return;
        }
        // Keep the saved grid as it is; only append defaults never offered.
        const merged = appendNewDefaults(hydrated, this.featureAccess, layoutStore.load(this.offeredKey));
        this.layout = merged.layout;
        layoutStore.save(this.offeredKey, merged.offered);
        if (merged.layout.length !== hydrated.length) this.persistLayout();
      },
      persistLayout() {
        layoutStore.save(
          this.storageKey,
          this.layout.map((i) => ({ i: i.i, x: i.x, y: i.y, w: i.w, h: i.h }))
        );
      },
      onLayoutUpdated() {
        this.persistLayout();
        this.emitState();
      },
      resetLayout() {
        this.layout = buildDefaultLayout(this.featureAccess);
        this.persistLayout();
        this.emitState();
        this.loadAll();
      },
      addWidget(i) {
        if (!WIDGETS[i] || this.presentIds.has(i)) return;
        if (!isEntitled(i, this.featureAccess)) return;
        const maxY = this.layout.reduce((m, w) => Math.max(m, w.y + w.h), 0);
        this.layout = [...this.layout, widgetLayoutItem(i, 0, maxY)];
        this.persistLayout();
        this.emitState();
        this.loadAll();
      },
      removeWidget(i) {
        this.layout = this.layout.filter((w) => w.i !== i);
        this.persistLayout();
        this.emitState();
      },
      // Loads the orchestrated widgets (portfolio/today/blackout); self-fetching
      // ones reload off `reloadToken`. Returns a promise settling when they finish.
      loadAll() {
        const token = ++this.loadToken;
        const tags = this.tagIds;
        const plans = this.planTypes;
        const has = (i) => this.presentIds.has(i);
        const jobs = [];

        if (has('portfolio')) {
          jobs.push(this.load('portfolio', () => DashboardService.fetchPortfolio(tags, plans), token));
          // Separate job — never batch-primed, so it must not hold up the pie.
          jobs.push(this.load('archivedUrls', () => DashboardService.fetchArchivedUrlCount(tags), token));
        }
        if (has('today')) {
          jobs.push(this.load('today', () => DashboardService.fetchTodaysAlerts(tags, plans), token));
          // Only AI-Triage accounts render the severity chips, and the endpoint
          // enforces the same entitlement — asking anyway just earns a 403.
          if (this.aiTriage) {
            jobs.push(this.load('severityToday', () => DashboardService.fetchSeverityTally(1, tags, plans), token));
          } else {
            this.severityToday = { data: null, loading: false, error: false };
          }
        }
        if (has('blackout')) {
          jobs.push(this.load('blackout', () => DashboardService.fetchBlackoutCoverage(tags, plans), token));
        }
        return Promise.all(jobs);
      },
      // One descriptor per present widget (orchestrated built here, self-fetching
      // pulled off each instance), de-duped by path.
      collectDescriptors() {
        const tags = this.tagIds;
        const plans = this.planTypes;
        const has = (i) => this.presentIds.has(i);
        const R = DashboardService.resources;
        const items = [];
        if (has('portfolio')) items.push(R.portfolio(tags, plans));
        if (has('today')) {
          items.push(R.today(tags, plans));
          if (this.aiTriage) items.push(R.severityToday(tags, plans));
        }
        if (has('blackout')) items.push(R.blackout(tags, plans));
        // The batch carries the filters once at the root, so every item in it is a
        // filtered item — account-wide widgets fetch on their own key instead.
        const filtered = tags.length > 0 || plans.length > 0;
        ['totalTrend', 'topNoisy', 'trend', 'topPages', 'planUtil', 'fqdnTag'].forEach((id) => {
          if (!has(id)) return;
          if (filtered && ACCOUNT_WIDE.has(id)) return;
          const ref = this.$refs['w_' + id];
          const inst = Array.isArray(ref) ? ref[0] : ref;
          if (inst && inst.cacheDescriptors) items.push(...inst.cacheDescriptors());
        });
        const seen = new Set();
        return items.filter((it) => it && it.path && !seen.has(it.path) && seen.add(it.path));
      },
      // One request for all present widgets, primed into the cache so the reload
      // is served from cache. Best-effort: un-primed widgets fall back to fetching.
      async batchPrime() {
        const items = this.collectDescriptors();
        if (!items.length) return;
        try {
          const res = await DashboardService.loadDashboardBatch(items, this.tagIds, this.planTypes);
          const results = (res && Array.isArray(res.results)) ? res.results : [];
          results.forEach((r) => r && r.path && primeCache(r.path, r.data));
        } catch (e) {
          // Fall through — widgets fetch individually on the reload below and warning is logged.
          console.warn('DashboardGrid: batched metrics unavailable, falling back to per-widget fetches', e);
        }
      },
      // One batch, then reload every widget from the primed cache.
      async reloadBatched() {
        const token = ++this.loadToken;
        // Watchers run before this component's render, so children still hold the
        // previous props — descriptors built now would be for the wrong keys.
        await this.$nextTick();
        await this.batchPrime();
        // Superseded during the slow batch (reset/add/remove/filter) — abort tail.
        if (token !== this.loadToken) return null;
        this.reloadToken += 1; // self-fetching widgets watch this and reload
        return this.loadAll();
      },
      // Refresh also drops the cache, so the batch refetches rather than re-primes.
      async refresh() {
        clearMetricsCache();
        return this.reloadBatched();
      },
      // Drops the result if a newer load context started before it resolved.
      async load(key, fetcher, token) {
        this[key].loading = true;
        this[key].error = false;
        try {
          const result = await fetcher();
          if (token !== this.loadToken) return null;
          this[key].data = result;
          return result;
        } catch (e) {
          if (token === this.loadToken) this[key].error = true;
          return null;
        } finally {
          if (token === this.loadToken) this[key].loading = false;
        }
      }
    }
  };
</script>

<style lang="scss">
  @import './dashboard-widgets.scss';
</style>
